import { defineStore } from 'pinia';
import { ref } from 'vue';
import { getSdk } from '@/composables/useSdk';
import type { Branding, BrandingIcons } from '@cc-consulting-nv/ccsdk';

// Re-export types for consumers
export type { Branding, BrandingIcons };

export const useSettingsStore = defineStore('settings', () => {
  // State
  const branding = ref<Branding | null>(null);
  const isLoading = ref(false);
  const error = ref<string | null>(null);
  const isInitialized = ref(false);

  // Actions
  async function fetchBranding(): Promise<void> {
    if (isLoading.value) return;

    isLoading.value = true;
    error.value = null;

    try {
      const sdk = getSdk();
      branding.value = await sdk.brandingGet();
      isInitialized.value = true;
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to fetch branding';
      console.error('Failed to fetch branding:', err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  async function initialize(): Promise<void> {
    if (isInitialized.value) return;
    await fetchBranding();
  }

  function getSiteName(): string {
    return branding.value?.description || 'Gunclub';
  }

  function getShortName(): string {
    return branding.value?.shortName || 'gunclub';
  }

  function getSiteLogo(): string | null {
    return branding.value?.icons['192'] || null;
  }

  return {
    // State
    branding,
    isLoading,
    error,
    isInitialized,

    // Actions
    initialize,
    fetchBranding,
    getSiteName,
    getShortName,
    getSiteLogo,
  };
});

export type SettingsStore = ReturnType<typeof useSettingsStore>;
